import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './Modal.module.scss';

/**
 * Every modal open right now, outermost first. Scroll lock and Escape belong to the stack rather
 * than to each instance: a modal opened on top of another must not hand the page scroll back when
 * it alone closes, and Escape should dismiss one layer at a time.
 */
const openModals: object[] = [];

interface ModalProps {
  open: boolean;
  onClose: () => void;
  size?: 'default' | 'wide' | 'extra-wide' | 'xx-wide';
  showCloseX?: boolean;
  /**
   * Close the modal when the user clicks the backdrop. Defaults to true.
   * Set to false for multi-step flows where an accidental backdrop click
   * would lose unsaved progress (e.g. the CSV import wizard).
   */
  dismissOnBackdropClick?: boolean;
  className?: string;
  backdropClassName?: string;
  children: React.ReactNode;
}

export default function Modal({
  open,
  onClose,
  size = 'default',
  showCloseX = true,
  dismissOnBackdropClick = true,
  className,
  backdropClassName,
  children,
}: ModalProps) {
  // Read through a ref so the effect below keys off `open` alone. Callers pass an inline arrow, so
  // depending on `onClose` would re-run it every render and churn the stack.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) {
      return;
    }
    const token = {};
    openModals.push(token);
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openModals[openModals.length - 1] === token) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openModals.indexOf(token);
      if (index !== -1) {
        openModals.splice(index, 1);
      }
      if (openModals.length === 0) {
        document.body.style.overflow = '';
      }
    };
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!dismissOnBackdropClick) {
      return;
    }
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  const sizeClass =
    size === 'wide' ? styles.wide : size === 'extra-wide' ? styles.extraWide : size === 'xx-wide' ? styles.xxWide : '';

  return ReactDOM.createPortal(
    <div
      className={`${styles.backdrop} ${styles.open} ${sizeClass} ${backdropClassName ?? ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`${styles.modal} ${className ?? ''}`}>
        {showCloseX && (
          <button type="button" className={styles.closeX} onClick={onClose} aria-label="Close">
            &times;
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/** Modal title */
export function ModalTitle({ children }: { children: React.ReactNode }) {
  return <h2 className={styles.title}>{children}</h2>;
}

/** Modal subtitle/description */
export function ModalSubtitle({ children }: { children: React.ReactNode }) {
  return <p className={styles.subtitle}>{children}</p>;
}

/** Modal action buttons row */
export function ModalActions({ children }: { children: React.ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}

/**
 * A lower-priority action on its own row beneath {@link ModalActions}. Keeps a long label
 * ("Schedule for later") from widening the button row past the modal.
 */
export function ModalActionsSecondary({ children }: { children: React.ReactNode }) {
  return <div className={styles.actionsSecondary}>{children}</div>;
}
